import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AdminMachine,
  AdminMachinesQuery,
  getAdminMachines as getAdminMachinesApi,
  setBotActivation as setBotActivationApi
} from "./api/adminMachinesApi";
import { Page } from "../shared/models/models";

export interface AdminMachinesState {
  query: AdminMachinesQuery;
  machines: Page<AdminMachine>;
  loading: boolean;
  success?: string;
  error: any;
}

const initialState: AdminMachinesState = {
  query: {
    pageNo: 1,
    pageSize: 20,
    sortBy: 5, // CreatedDate
    sortDesc: true
  },
  machines: {
    items: [],
    totalCount: 0,
    totalPages: 0,
    pageNo: 1,
    pageSize: 20
  },
  loading: false,
  success: undefined,
  error: null
};

// Async thunks
export const getAdminMachines = createAsyncThunk(
  "adminMachines/getAdminMachines",
  async (query: AdminMachinesQuery) => {
    return await getAdminMachinesApi(query);
  }
);

export const setBotActivation = createAsyncThunk(
  "adminMachines/setBotActivation",
  async (params: { id: string; isActivated: boolean }) => {
    await setBotActivationApi(params.id, params.isActivated);
    return params;
  }
);

// Slice
const adminMachinesSlice = createSlice({
  name: "adminMachines",
  initialState,
  reducers: {
    changeQuery: (state, action: PayloadAction<AdminMachinesQuery>) => {
      state.query = action.payload;
    },
    setSuccess: (state, action: PayloadAction<string>) => {
      state.success = action.payload;
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = undefined;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Get machines
    builder.addCase(getAdminMachines.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAdminMachines.fulfilled, (state, action) => {
      state.loading = false;
      state.machines = action.payload;
    });
    builder.addCase(getAdminMachines.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Set bot activation
    builder.addCase(setBotActivation.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(setBotActivation.fulfilled, (state, action) => {
      state.loading = false;
      // Update the machine in the list
      const machine = state.machines.items.find(
        (m) => m.id === action.payload.id
      );
      if (machine) {
        machine.botActivated = action.payload.isActivated;
      }
    });
    builder.addCase(setBotActivation.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  }
});

export const { changeQuery, setSuccess, clearSuccess, clearError } =
  adminMachinesSlice.actions;

export default adminMachinesSlice.reducer;
